<script setup lang="ts">
import { computed, type PropType, ref, watch, onUnmounted } from 'vue';
import { Icon } from '@iconify/vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import type { ProductionByQualityReportResponse } from '@/types/report-types';
import { getProductById } from '@/api/products';

const props = defineProps({
  visible: { type: Boolean, required: true },
  onClose: { type: Function as PropType<() => void>, required: true },
  reportData: { type: Object as PropType<ProductionByQualityReportResponse | null>, default: null },
  startDate: { type: Date as PropType<Date | null>, default: null },
  endDate: { type: Date as PropType<Date | null>, default: null },
  qualityId: { type: String as PropType<string | null>, default: null },
  companyName: { type: String, default: 'Techknital Fabrics Ltd' },
  companyAddress: { type: String, default: 'Unit 8 - 9, Lime Kilns Business Park\nLime Kilns Way\nHinckley\nLeicestershire, LE10 #EL' },
  companyTel: { type: String, default: '+44 (0)1455 254350' },
  companyFax: { type: String, default: '+44 (0)1455 238998' },
});

const PDF_EXPORT_ELEMENT_ID = 'production-by-quality-report-content';

const qualityDetailsMap = ref<Map<string, { name: string | null, isLoading: boolean }>>(new Map());

const isLoadingAnyQuality = computed(() => {
  return [...qualityDetailsMap.value.values()].some(detail => detail.isLoading);
});

const formattedStartDate = computed(() =>
    props.startDate?.toLocaleDateString('en-GB') ?? 'N/A'
);

const formattedEndDate = computed(() =>
    props.endDate?.toLocaleDateString('en-GB') ?? 'N/A'
);

const onDownloadReportPdf = () => {
  const reportElement = document.getElementById(PDF_EXPORT_ELEMENT_ID);
  if (!reportElement || !props.reportData) return;

  const qualityName = getQualityDisplayName(props.qualityId || '').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  const filename = `production-by-quality-${qualityName}-${new Date().toISOString().split('T')[0]}.pdf`;

  html2pdf()
      .set({
        margin: [10, 5, 10, 5],
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      })
      .from(reportElement)
      .save();
};

const fetchQualityNames = async () => {
  if (!props.reportData?.qualities || !props.qualityId) {
    qualityDetailsMap.value.clear();
    return;
  }

  const mapCopy = new Map(qualityDetailsMap.value);

  if (!mapCopy.has(props.qualityId) || mapCopy.get(props.qualityId)?.name === null) {
    mapCopy.set(props.qualityId, { name: null, isLoading: true });
    qualityDetailsMap.value = new Map(mapCopy);

    try {
      const product = await getProductById(props.qualityId);
      mapCopy.set(props.qualityId, {
        name: product.unfinishedReference || product.finishedReference || "Unknown Quality",
        isLoading: false
      });
    } catch {
      mapCopy.set(props.qualityId, {
        name: "Error: Quality N/A",
        isLoading: false
      });
    }

    qualityDetailsMap.value = new Map(mapCopy);
  }
};

watch(() => props.reportData, (newData) => {
  if (props.visible && newData) {
    qualityDetailsMap.value.clear();
    fetchQualityNames();
  }
}, { deep: true });

watch(() => props.visible, (visible) => {
  if (visible && props.reportData && qualityDetailsMap.value.size === 0) {
    fetchQualityNames();
  }
});

const getQualityDisplayName = (qualityId: string): string => {
  const detail = qualityDetailsMap.value.get(qualityId);
  return detail?.isLoading ? 'Loading...' : (detail?.name || 'Quality N/A');
};

const hasReportData = computed(() =>
    !!(props.reportData?.qualities?.length)
);

const tableData = computed(() => {
  if (!props.reportData?.qualities?.length) return [];

  const quality = props.reportData.qualities[0];
  return quality.batches.flatMap(batch =>
      batch.rolls.map(roll => ({
        ...roll,
        batchNumber: batch.batchNumber,
        dateKnitted: new Date(batch.dateKnitted).toLocaleDateString('en-GB'),
        rollWeight: roll.weight,
        rollLength: roll.length,
        batchWeight: batch.weight,
        batchLength: batch.length
      }))
  );
});

onUnmounted(() => {
  qualityDetailsMap.value.clear();
});
</script>

<template>
  <Dialog
      :visible="visible"
      modal
      :draggable="false"
      :style="{ width: '75rem', maxWidth: '95vw', minHeight: '90vh'}"
      :closable="false"
      @update:visible="onClose"
      class="report-dialog-custom-header"
  >
    <template #header>
      <div class="flex justify-between items-center w-full">
        <Button
            link
            class="flex items-center gap-x-1 p-0 text-gray-600 hover:text-primary-default"
            @click="onClose"
            aria-label="Back to reports filter"
        >
          <Icon icon="material-symbols:arrow-back-rounded" class="text-2xl md:text-3xl group-hover:-translate-x-1 transition-transform" />
          <span class="text-sm md:text-base hidden sm:inline">Back to Report Page</span>
        </Button>
        <div class="flex-grow text-center">
          <h2 class="text-lg md:text-xl font-semibold">Production by Quality Report</h2>
        </div>
        <div class="flex items-center gap-x-2">
          <Button
              label="Download as PDF"
              class="p-button-danger text-sm md:text-base"
              @click="onDownloadReportPdf"
              :disabled="!hasReportData || isLoadingAnyQuality"
          >
            <template #icon>
              <Icon icon="material-symbols:download-sharp" class="text-base md:text-lg mr-1 md:mr-2" />
            </template>
          </Button>
        </div>
      </div>
    </template>

    <div class="dialog-main-content flex-grow overflow-y-auto">
      <div v-if="(!reportData && visible) || (isLoadingAnyQuality && hasReportData)" class="text-center py-10 text-gray-500">
        {{ isLoadingAnyQuality ? 'Loading quality details...' : 'Loading report data or no data available...' }}
      </div>
      <div v-else-if="hasReportData" :id="PDF_EXPORT_ELEMENT_ID" class="p-2 md:p-4 bg-white">
        <!-- Company Info -->
        <div class="flex justify-between items-start mb-6">
          <div>
            <img src="@/assets/images/logo.png" alt="Company Logo" class="h-12 mb-1" />
          </div>
          <div class="text-right text-xs text-gray-500 whitespace-pre-line">
            <p>{{ companyAddress }}</p>
            <p>Tel: {{ companyTel }}</p>
            <p>Fax: {{ companyFax }}</p>
          </div>
        </div>

        <!-- Dates and Report Type Block -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-md shadow-sm">
          <div>
            <h3 class="text-md font-semibold text-gray-700">Dates</h3>
            <p class="text-sm text-gray-600">Start Date: {{ formattedStartDate }}</p>
            <p class="text-sm text-gray-600">End Date: {{ formattedEndDate }}</p>
          </div>
          <div class="md:text-right">
            <h3 class="text-md font-semibold text-gray-700">Production by Quality</h3>
            <p class="text-sm text-gray-600">{{ getQualityDisplayName(qualityId || '') }}</p>
          </div>
        </div>

        <!-- Report Details -->
        <div>
          <div class="mb-8">
            <div class="report-detail-header mb-3 pb-2 border-b border-gray-300">
              <h4 class="text-lg font-semibold text-gray-800">Report Detail</h4>
              <p class="text-sm text-gray-600">Quality: {{ getQualityDisplayName(qualityId || '') }}</p>
            </div>

            <DataTable
                :value="tableData"
                responsiveLayout="scroll"
                class="p-datatable-sm"
                stripedRows
            >
              <Column field="dateKnitted" header="Date Knitted" sortable style="width: 12%"></Column>
              <Column field="rollNumber" header="Roll Number" sortable style="width: 12%"></Column>
              <Column field="batchNumber" header="Batch Number" sortable style="width: 30%">
                <template #body="slotProps">
                  {{ slotProps.data.batchNumber }}
                </template>
              </Column>
              <Column header="Roll Weight" sortable field="weight" style="width: 13%">
                <template #body="slotProps">{{ slotProps.data.weight.toFixed(2) }} kg</template>
              </Column>
              <Column header="Roll Length" sortable field="length" style="width: 13%">
                <template #body="slotProps">{{ slotProps.data.length.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) }} m</template>
              </Column>

              <ColumnGroup type="footer">
                <Row>
                  <Column footer="Totals:" :colspan="3" footerStyle="text-align:left; font-weight: 600; color: #374151;" />
                  <Column :footer="reportData?.totalRollWeight.toFixed(2) + ' kg'" footerStyle="text-align:left; font-weight: 600; color: #374151;" />
                  <Column :footer="reportData?.totalRollLength.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' m'" footerStyle="text-align:left; font-weight: 600; color: #374151;" />
                  <Column footer="" footerStyle="text-align:left; font-weight: 600; color: #374151;" />
                </Row>
              </ColumnGroup>
            </DataTable>
          </div>
        </div>
      </div>
      <div v-else-if="!hasReportData && visible" class="text-center py-10 text-gray-500">
        No production data found for the selected criteria.
      </div>
    </div>
  </Dialog>
</template>

<style scoped></style>
